<template>
  <v-flex
    id="cashReceipts"
    class="question"
  >
    <p class="mb-0 text-uppercase">
      {{ $t('sheet.sheetOne.hint_material') }}
    </p>
    <h2 class="display-3 display-3_dark">
      {{ getCashReceiptsQuestions.label }}
    </h2>
    <ul class="questions-list">
      <li
        v-if="getCashReceiptsQuestions.hasOwnProperty('is_members_work')"
        class="mt-4"
      >
        <p
          class="header"
          :class="{ required: getCashReceiptsQuestions.is_members_work.required }"
        >
          {{ getCashReceiptsQuestions.is_members_work.label }}
        </p>
        <ConfirmButtons
          v-model="isMembersWork"
          v-validate="{ required: getCashReceiptsQuestions.is_members_work.required }"
          string
          :error-messages="errors.collect('isMembersWork')"
          :data-vv-as="$t('label.question')"
          :data-vv-name="'isMembersWork'"
          :disabled="readOnly"
        />
      </li>
      <li
        v-if="isMembersWork === 'yes' && getCashReceiptsQuestions.hasOwnProperty('active_members')"
        class="mt-4"
      >
        <p
          class="header custom lA"
          :class="{ required: getCashReceiptsQuestions.active_members.required }"
        >
          {{ getCashReceiptsQuestions.active_members.label }}
        </p>
        <v-checkbox
          v-for="(item, index) in members"
          :key="index"
          v-model="activeMembers"
          v-validate="{ required: getCashReceiptsQuestions.active_members.required }"
          :label="item.name"
          :value="item.id"
          type="checkbox"
          multiple
          :error="errors.has('activeMembers')"
          :error-messages="index + 1 === members.length ? errors.collect('activeMembers') : null"
          :data-vv-as="$t('label.question')"
          :data-vv-name="'activeMembers'"
          :disabled="readOnly"
        />
        <ActiveMembersForm
          v-if="getCashReceiptsQuestions.hasOwnProperty('members')"
          v-show="activeMembers.length > 0"
          v-model="totalAmount"
          :read-only="readOnly"
          :items="members"
          :selected="activeMembers"
        />
        <MoneySummary
          :title="getCashReceiptsQuestions.total_receipts_amount.label"
          :value="totalAmount"
        />
      </li>
      <li
        v-if="getCashReceiptsQuestions.hasOwnProperty('notice_period')"
        class="mt-4"
      >
        <p
          class="header"
          :class="{ required: getCashReceiptsQuestions.notice_period.required }"
        >
          {{ getCashReceiptsQuestions.notice_period.label }}
        </p>
        <ConfirmButtons
          v-model="noticePeriod"
          v-validate="{ required: getCashReceiptsQuestions.notice_period.required }"
          string
          :error-messages="errors.collect('noticePeriod')"
          data-vv-as=" "
          :data-vv-name="'noticePeriod'"
          :disabled="readOnly"
        />
      </li>
      <li
        v-if="
          noticePeriod === 'yes' && getCashReceiptsQuestions.hasOwnProperty('notice_period_members')
        "
        class="mt-4"
      >
        <p
          class="header custom lA"
          :class="{ required: getCashReceiptsQuestions.notice_period_members.required }"
        >
          {{ getCashReceiptsQuestions.notice_period_members.label }}
        </p>
        <v-checkbox
          v-for="(item, index) in noticePeriodMembers"
          :key="index"
          v-model="noticeMembersList"
          v-validate="{ required: getCashReceiptsQuestions.notice_period_members.required }"
          class="column column_full"
          :label="item.name"
          :value="item.id"
          type="checkbox"
          multiple
          :error="errors.has('noticePeriodMembersConfirmation')"
          :error-messages="
            index + 1 === members.length ? errors.collect('noticePeriodMembersConfirmation') : null
          "
          data-vv-as=" "
          :data-vv-name="`noticePeriodMembersConfirmation`"
          :disabled="readOnly"
        >
          <template slot="append">
            <v-textarea
              v-if="item.confirmation"
              v-model="item.description"
              v-validate="{
                ...requiredRegularField(getCashReceiptsQuestions.notice_period_members.required)
              }"
              :placeholder="$t('placeholders.add_text')"
              :error-messages="errors.collect(`noticePeriodMembers${item.id}`)"
              data-vv-as=" "
              :data-vv-name="`noticePeriodMembers${item.id}`"
              :required="getCashReceiptsQuestions.notice_period_members.required"
              class="small"
              auto-grow
              :disabled="readOnly"
            />
          </template>
        </v-checkbox>
      </li>
      <li
        v-if="getCashReceiptsQuestions.hasOwnProperty('chosen_sources')"
        class="mt-4"
      >
        <p class="header">
          <span :class="{ required: getCashReceiptsQuestions.chosen_sources.required }">
            {{ getCashReceiptsQuestions.chosen_sources.label }}</span>
          <a
            :disabled="!isCurrentEdition"
            target="_blank"
            :href="links.RULES_FOR_BENEFITS"
          >{{
            $t('sheet.sheetOne.meeting.cash_receipts.link')
          }}</a>
        </p>
        <v-layout wrap>
          <v-flex
            v-for="(item, index) in incomeSources"
            :key="index"
            md6
            sm12
            xs12
          >
            <v-checkbox
              v-model="chosenSources"
              v-validate="{ required: getCashReceiptsQuestions.chosen_sources.required }"
              :label="item.description"
              :value="item.id"
              type="checkbox"
              multiple
              :disabled="isDisabled(chosenSources, item.id, 316) || readOnly"
              :error="errors.has('chosenSources')"
              :error-messages="
                index + 1 === incomeSources.length ? errors.collect('chosenSources') : null
              "
              :data-vv-as="$t('label.question')"
              :data-vv-name="'chosenSources'"
              :class="[{ 'text-dark underlined': item.id === 316 }]"
            />
          </v-flex>
        </v-layout>
        <div
          v-if="
            showOtherSourcesField &&
              getCashReceiptsQuestions.hasOwnProperty('chosen_source_other_description')
          "
          class="mt-3"
        >
          <p
            class="fw-600"
            :class="{ required: getCashReceiptsQuestions.chosen_source_other_description.required }"
          >
            {{ getCashReceiptsQuestions.chosen_source_other_description.label }}
          </p>
          <v-textarea
            v-model="chosenSourceOtherDescription"
            v-validate="{
              ...requiredRegularField(
                getCashReceiptsQuestions.chosen_source_other_description.required
              )
            }"
            :placeholder="$t('placeholders.add_text')"
            :error-messages="errors.collect('another_sources')"
            :data-vv-as="getCashReceiptsQuestions.chosen_source_other_description.label"
            :data-vv-name="'another_sources'"
            :required="getCashReceiptsQuestions.chosen_source_other_description.required"
            class="pt-0 mt-0"
            :disabled="readOnly"
            :rows="1"
            auto-grow
          />
        </div>
        <v-layout
          v-if="showOtherIncomes"
          class="my-3"
          align-center
          wrap
        >
          <p class="fw-600 required mr-2 mb-0">
            {{ getCashReceiptsQuestions.chosen_source_details.label }}
          </p>
        </v-layout>
        <AnotherIncomes
          v-if="
            showOtherIncomes && getCashReceiptsQuestions.hasOwnProperty('chosen_source_details')
          "
          :items="chosenSourceDetails"
          :selected="chosenSources"
          :read-only="readOnly"
          :selected-members="representatives"
          :incomes="incomeSources"
          @emitCheck="getTotalChosenSource"
        />
        <MoneySummary
          :title="getCashReceiptsQuestions.total_another_receipts_amount.label"
          :value="totalAnotherIncomes"
        />
      </li>
      <li
        v-if="
          showCustomField && getCashReceiptsQuestions.hasOwnProperty('family_500_plus_improvement')
        "
        class="mt-4"
      >
        <p
          class="header custom lA"
          :class="{ required: getCashReceiptsQuestions.family_500_plus_improvement.required }"
        >
          {{ getCashReceiptsQuestions.family_500_plus_improvement.label }}
        </p>
        <v-textarea
          v-model="family500plusImprovement"
          v-validate="{
            ...requiredRegularField(getCashReceiptsQuestions.family_500_plus_improvement.required)
          }"
          :placeholder="$t('placeholders.add_text')"
          :error-messages="errors.collect('required_regular_field_20')"
          :data-vv-as="$t('label.question')"
          :data-vv-name="'required_regular_field_20'"
          :required="getCashReceiptsQuestions.family_500_plus_improvement.required"
          class="small"
          auto-grow
          :disabled="readOnly"
        />
      </li>
      <li
        v-if="getCashReceiptsQuestions.hasOwnProperty('other_help')"
        class="mt-4"
      >
        <p
          class="header"
          :class="{ required: getCashReceiptsQuestions.other_help.required }"
        >
          {{ getCashReceiptsQuestions.other_help.label }}
        </p>
        <ConfirmButtons
          v-model="otherHelp"
          v-validate="{ required: getCashReceiptsQuestions.other_help.required }"
          string
          :error-messages="errors.collect('otherHelp')"
          :data-vv-as="$t('label.question')"
          :data-vv-name="'otherHelp'"
          :disabled="readOnly"
        />
      </li>
      <li
        v-if="otherHelp === 'yes' && getCashReceiptsQuestions.hasOwnProperty('other_help_explain')"
        class="mt-4"
      >
        <p
          class="header custom lA"
          :class="{ required: getCashReceiptsQuestions.other_help_explain.required }"
        >
          {{ getCashReceiptsQuestions.other_help_explain.label }}
        </p>
        <v-textarea
          v-model="otherHelpExplain"
          v-validate="{
            ...requiredRegularField(getCashReceiptsQuestions.other_help_explain.required)
          }"
          :placeholder="$t('placeholders.add_text')"
          :error-messages="errors.collect('otherHelpExplain')"
          :data-vv-as="$t('label.question')"
          :data-vv-name="'otherHelpExplain'"
          :required="getCashReceiptsQuestions.other_help_explain.required"
          auto-grow
          :disabled="readOnly"
        />
      </li>
      <SheetQuestion
        v-if="getCashReceiptsQuestions.hasOwnProperty('help_from_family_and_friends')"
        :label="getCashReceiptsQuestions?.help_from_family_and_friends?.label"
        :required="getCashReceiptsQuestions?.help_from_family_and_friends?.required"
      >
        <ConfirmButtons
          v-model="helpFromFamilyAndFriends"
          v-validate="{ required: getCashReceiptsQuestions.help_from_family_and_friends.required }"
          string
          :error-messages="errors.collect('helpFromFamilyAndFriends')"
          :data-vv-as="$t('label.question')"
          :data-vv-name="'helpFromFamilyAndFriends'"
          :disabled="readOnly"
        />
      </SheetQuestion>
      <SheetQuestion
        v-if="
          getCashReceiptsQuestions.hasOwnProperty('help_from_family_and_friends_description') &&
            helpFromFamilyAndFriends === 'yes'
        "
        :label="getCashReceiptsQuestions.help_from_family_and_friends_description.label"
        :required="getCashReceiptsQuestions.help_from_family_and_friends_description.required"
        custom
        list-sign="lA"
      >
        <v-textarea
          v-model="helpFromFamilyAndFriendsDescription"
          v-validate="{
            ...regularLongText(
              getCashReceiptsQuestions.help_from_family_and_friends_description.required
            )
          }"
          :placeholder="$t('placeholders.add_text')"
          :error-messages="errors.collect('helpFromFamilyAndFriendsDescription')"
          :data-vv-as="$t('label.question')"
          :data-vv-name="'helpFromFamilyAndFriendsDescription'"
          :required="getCashReceiptsQuestions.help_from_family_and_friends_description.required"
          auto-grow
          :disabled="readOnly"
        />
      </SheetQuestion>
      <SheetQuestion
        v-if="getCashReceiptsQuestions.hasOwnProperty('has_savings')"
        :label="getCashReceiptsQuestions.has_savings.label"
        :required="getCashReceiptsQuestions.has_savings.required"
      >
        <ConfirmButtons
          v-model="hasSavings"
          v-validate="{ required: getCashReceiptsQuestions.has_savings.required }"
          string
          :error-messages="errors.collect('hasSavings')"
          :data-vv-as="$t('label.question')"
          :data-vv-name="'hasSavings'"
          :disabled="readOnly"
        />
      </SheetQuestion>
      <SheetQuestion
        v-if="
          getCashReceiptsQuestions.hasOwnProperty('sustained_by_savings') && hasSavings === 'yes'
        "
        :label="getCashReceiptsQuestions.sustained_by_savings.label"
        :required="getCashReceiptsQuestions.sustained_by_savings.required"
        custom
        list-sign="lA"
      >
        <BaseRadio
          v-model="sustainedBySavings"
          :required="getCashReceiptsQuestions?.sustained_by_savings?.required"
          name="sustainedBySavings"
          :values="sustainedBySavingsResource"
          value-key="id"
          label-key="description"
          :disabled="readOnly"
        />
      </SheetQuestion>
    </ul>
  </v-flex>
</template>

<script>
import ConfirmButtons from '@/components/widgets/sheet/ConfirmButtons';
import ActiveMembersForm from '@/components/widgets/sheet/questions/cashReceipts/ActiveMembersForm';
import AnotherIncomes from '@/components/widgets/sheet/questions/cashReceipts/AnotherIncomes';
import MoneySummary from '@/components/widgets/sheet/moneySummary';

import validationRules from '@/constants/validationRules';
import links from '@/constants/links';

import formatDecimalVariables from '@/mixins/formatDecimalVariables';
import sheetValidationRules from '@/mixins/sheet/sheetValidationRules';
import sheetResourcesGetter from '@/mixins/sheet/sheetResourcesGetter';

import { mapGetters } from 'vuex';
import { createHelpers } from 'vuex-map-fields';

import debounce from 'lodash.debounce';
import SheetQuestion from '@/components/sheet/SheetQuestion.vue';
import BaseRadio from '@/components/widgets/BaseRadio.vue';

const { mapFields } = createHelpers({
    getterType: 'sheet/getCashReceiptsField',
    mutationType: 'sheet/updateCashReceiptsField'
});

export default {
    name: 'CashReceipts',
    components: {
        ConfirmButtons,
        ActiveMembersForm,
        MoneySummary,
        AnotherIncomes,
        SheetQuestion,
        BaseRadio
    },
    mixins: [formatDecimalVariables, sheetValidationRules, sheetResourcesGetter],
    inject: ['$validator'],
    props: {
        readOnly: {
            default: false,
            type: Boolean
        }
    },
    data () {
        return {
            validationRules,
            links,
            emptyMembersData: {
                position: null,
                dimension: null,
                income: 0,
                complete: false
            },
            totalAmount: 0,
            incomeSources: [],
            noticeMembersList: []
        };
    },
    computed: {
        ...mapFields([
            'isMembersWork',
            'activeMembers',
            'members',
            'chosenSources',
            'chosenSourceDetails',
            'otherHelp',
            'otherHelpExplain',
            'totalReceiptsAmount',
            'totalAnotherReceiptsAmount',
            'chosenSourceOtherDescription',
            'family500plusImprovement',
            'noticePeriod',
            'noticePeriodMembers',
            'helpFromFamilyAndFriends',
            'helpFromFamilyAndFriendsDescription',
            'hasSavings',
            'sustainedBySavings'
        ]),
        ...mapGetters('sheet', ['getCashReceiptsQuestions']),
        totalAnotherIncomes () {
            return this.totalAnotherReceiptsAmount;
        },
        representatives () {
            return this.$store.getters['sheet/getRepresentatives'];
        },
        showCustomField () {
            return this.chosenSources.includes(102);
        },
        showOtherSourcesField () {
            return this.chosenSources.includes(103);
        },
        showOtherIncomes () {
            return !this.chosenSources.includes(316);
        },
        isCurrentEdition () {
            return this.$store.getters['family/isCurrentEdition'];
        },
        removeRepresentativeAction () {
            return this.$store.getters['sheet/getRemoveRepresentative'];
        },
        sustainedBySavingsResource () {
            return this.getResources(this.getCashReceiptsQuestions?.sustained_by_savings) ?? [];
        }
    },
    watch: {
        helpFromFamilyAndFriends (val) {
            if (val === 'no') {
                this.helpFromFamilyAndFriendsDescription = null;
            }
        },
        hasSavings (val) {
            if (val === 'no') {
                this.sustainedBySavings = null;
            }
        },
        showCustomField (val) {
            if (val === false) {
                this.family500plusImprovement = null;
            }
        },
        showOtherSourcesField (val) {
            if (val === false) {
                this.chosenSourceOtherDescription = null;
            }
        },
        otherHelp (val) {
            if (val === 'no') {
                this.otherHelpExplain = null;
            }
        },
        totalAmount: {
            handler (val) {
                this.totalReceiptsAmount = parseFloat(val.toFixed(2));
            },
            immediate: true
        },
        representatives: {
            handler (val) {
                this.createMembers(val);
            },
            deep: true
        },
        removeRepresentativeAction: {
            handler (val) {
                if (val) {
                    this.changeMembersInChosenSourceDetails();
                }
            }
        },
        chosenSources (val) {
            val.forEach((e) => {
                if (
                    !this.chosenSourceDetails.find((el) => {
                        return el.chosenSource === e;
                    }) &&
          e !== 316
                ) {
                    this.chosenSourceDetails.push({
                        members: [],
                        chosenSource: e,
                        amount: null,
                        note: null
                    });
                    this.chosenSourceDetails.sort((a, b) => {
                        return a.chosenSource - b.chosenSource;
                    });
                }
            });
            this.chosenSourceDetails = this.chosenSourceDetails.filter((e) => {
                return val.includes(e.chosenSource);
            });
            this.getTotalChosenSource();
        },
        isMembersWork (val) {
            if (val === 'no') {
                this.$nextTick(() => {
                    this.activeMembers = [];
                });
            }
        },
        noticePeriod: {
            handler (val) {
                if (val === 'no') {
                    this.$nextTick(() => {
                        this.noticeMembersList = [];
                        this.noticePeriodMembers.forEach((el) => {
                            el.confirmation = false;
                            el.description = null;
                        });
                    });
                }
            }
        },
        activeMembers (valN, valO) {
            if (valN.length < valO.length) {
                this.members.forEach((el) => {
                    if (!valN.includes(el.id)) {
                        for (const prop in el) {
                            if (Object.prototype.hasOwnProperty.call(el, prop)) {
                                if (prop === 'name' || prop === 'id') {
                                    continue;
                                }
                                if (Array.isArray(el[prop])) {
                                    el[prop] = [];
                                } else {
                                    el[prop] = null;
                                }
                            }
                        }
                    }
                });
            }
        },
        noticeMembersList: {
            handler (val) {
                this.noticePeriodMembers.forEach((el) => {
                    el.confirmation = val.includes(el.id);
                    if (!el.confirmation) {
                        el.description = null;
                    }
                });
            }
        }
    },
    methods: {
        getTotalChosenSource () {
            let _sum = 0;
            this.chosenSourceDetails.forEach((el) => {
                if (el.amount && !isNaN(this.formatDecimalVariable(el.amount))) {
                    _sum += this.formatDecimalVariable(el.amount);
                }
            });
            this.totalAnotherReceiptsAmount = parseFloat(_sum.toFixed(2));
        },
        createMembers: debounce(
            function (val) {
                if (val != null && val.length > 0) {
                    val.forEach((e) => {
                        if (
                            this.members.find((el) => {
                                return el.id === e.id;
                            })
                        ) {
                            const index = this.members.findIndex((el) => {
                                return el.id === e.id;
                            });
                            const { position, income, dimension } = this.members[index];
                            this.members[index] = {
                                id: e.id,
                                name: e.firstName,
                                position,
                                income,
                                dimension
                            };
                        } else {
                            this.members.push({
                                id: e.id,
                                name: e.firstName,
                                ...this.emptyMembersData
                            });
                        }
                        if (this.noticePeriodMembers.find((el) => el.id === e.id)) {
                            const index = this.noticePeriodMembers.findIndex((el) => el.id === e.id);
                            const { description, confirmation } = this.noticePeriodMembers[index];
                            this.noticePeriodMembers[index] = {
                                id: e.id,
                                name: e.firstName,
                                description,
                                confirmation
                            };
                        } else {
                            this.noticePeriodMembers.push({
                                id: e.id,
                                name: e.firstName,
                                description: null,
                                confirmation: null
                            });
                        }
                    });
                    const _id = Array.from(val, (x) => x.id);
                    this.members = this.members.filter((member) => {
                        return _id.includes(member.id);
                    });
                    this.activeMembers = this.activeMembers.filter((selected) => {
                        return _id.includes(selected);
                    });
                    this.noticePeriodMembers = this.noticePeriodMembers.filter((member) =>
                        _id.includes(member.id)
                    );
                    this.noticeMembersList = this.noticeMembersList.filter((el) => _id.includes(el));
                }
            },
            1000,
            { leading: 1000 }
        ),
        isDisabled (model, id, excludeId) {
            if (model.includes(excludeId)) {
                if (model.includes(id) && id !== excludeId) {
                    model.splice(model.indexOf(id), 1);
                }
                return id !== excludeId;
            } else {
                return false;
            }
        },
        changeMembersInChosenSourceDetails () {
            this.chosenSourceDetails.forEach((el) => {
                el.members.forEach((member, index) => {
                    if (!this.representatives.find((item) => item.id === member)) {
                        el.members.splice(index, 1);
                    }
                });
            });
            this.$store.commit('sheet/setRemoveRepresentative', false);
        }
    },
    created () {
        this.getResources(this.getCashReceiptsQuestions.chosen_sources).forEach((el) => {
            this.incomeSources.push({
                persons: null,
                amount: null,
                note: null,
                complete: false,
                ...el
            });
        });
        this.createMembers(this.representatives);
        if (this.noticePeriodMembers && this.noticePeriodMembers.length > 0) {
            this.noticePeriodMembers.forEach((el) => {
                if (el.confirmation === true) {
                    this.noticeMembersList.push(el.id);
                }
            });
        }
    },
    beforeDestroy () {
        this.members = [];
        this.noticePeriodMembers = [];
    }
};
</script>

<style scoped></style>
